<template>
  <div>
    <PageTitle title="Document Type" />
    <v-container
      fluid
      class="lighten-12 content"
      style="background-color: white"
    >
      <ValidationObserver ref="observer" v-slot="{ validate, reset }">
        <v-btn :ripple="false" class="min-button" icon @click="show = !show">
          <v-icon color="blue" class="ml-10">{{
            show ? 'mdi-filter' : 'mdi-filter-outline'
          }}</v-icon>
          <span class="subheading filterclor">Filter</span>
        </v-btn>
        <v-expand-transition>
          <v-card class="lighten-12 card-content mb-2" v-show="show">
            <v-row class="ma-0 align-right">
              <TableFilters
                :filters="['status', 'search']"
                v-model="filter"
                :columns="columns"
                :msgs="'Name'"
              />
              <ImportExportMenu :permission="'Document Type Report'" />
            </v-row>
          </v-card>
        </v-expand-transition>

        <LoadingComponent v-if="isLoading" :visible="isLoading" />

        <v-card class="lighten-12 mt-2">
          <v-container fluid class="light gray lighten-12">
            <v-row>
              <v-card-text class="pa-0">
                <v-data-table
                  class="document-txt"
                  :items-per-page="paginationOptions.perPage"
                  :fixed-header="true"
                  :headers="headers"
                  :items="documenttypes"
                  item-key="id"
                  hide-default-footer
                >
                  <template v-slot:no-data>
                    <noData
                      class="mt-10 container justify-center item-center"
                      name="document type"
                    />
                  </template>

                  <template v-slot:item.name="{ item }">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Name"
                      vid="edit_name"
                      v-if="editDocumentTypeId === item.id"
                    >
                      <v-text-field
                        v-model="editDocumentType.name"
                        :error-messages="errors"
                        outlined
                        class="pt-3"
                      />
                    </ValidationProvider>
                    <p v-else>{{ item.name }}</p>
                  </template>

                  <template v-slot:item.status="{ item }">
                    <toggle-button
                      v-if="editDocumentTypeId === item.id"
                      v-model="editDocumentType.is_active"
                      :width="80"
                      :labels="{ checked: 'Active', unchecked: 'Archived' }"
                    />
                    <v-chip
                      v-else
                      :x-small="true"
                      class="ma-2"
                      label
                      text-color="white"
                      :color="getStatusColor(item.is_active)"
                      dark
                    >
                      {{ item.is_active ? 'Active' : 'Archived' }}
                    </v-chip>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <v-menu offset-y transition="scroll-y-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-show="editDocumentTypeId !== item.id"
                          v-bind="attrs"
                          v-on="on"
                          >mdi-dots-vertical</v-icon
                        >
                        <v-btn
                          v-show="editDocumentTypeId === item.id"
                          color="secondary"
                          fab
                          x-small
                          dark
                          class="ma-1"
                        >
                          <v-icon depressed small link @click="cancelEdit()"
                            >clear</v-icon
                          >
                        </v-btn>
                        <v-btn
                          v-show="editDocumentTypeId === item.id"
                          color="green"
                          fab
                          x-small
                          dark
                        >
                          <v-icon link @click="updateDocumentType()"
                            >done</v-icon
                          >
                        </v-btn>
                      </template>

                      <v-list class="actions">
                        <permission-control permissionName="Document Type Edit">
                          <v-list-item link @click="startEdit(item)">
                            <v-icon>mdi-pencil-box-outline</v-icon> Edit
                          </v-list-item>
                        </permission-control>
                        <permission-control
                          permissionName="Document Type Soft Delete"
                        >
                          <v-list-item link @click="openConfirmation(item)">
                            <v-icon>{{
                              item.is_active
                                ? 'mdi-archive'
                                : 'mdi-checkbox-marked-circle'
                            }}</v-icon>
                            {{ item.is_active ? 'Archive' : 'Activate' }}
                          </v-list-item>
                        </permission-control>
                      </v-list>
                    </v-menu>
                  </template>

                  <template v-slot:footer>
                    <pagination
                      @paginationOptions="setPaginationOptions"
                      @response="setResponseData"
                      @getPageNumber="setPagenumber"
                      :filterAction="true"
                      url="document-types"
                      :filter="filter"
                      ref="pagination"
                    />
                  </template>

                  <template slot="body.append">
                    <v-row v-if="!editDocumentTypeId">
                      <v-col cols="5">
                        <ValidationProvider
                          v-slot="{ errors }"
                          name="New Name"
                          vid="new_name"
                          rules="required"
                        >
                          <v-text-field
                            :error-messages="errors"
                            v-model="newDocumentType.name"
                            label="Name"
                            dense
                            outlined
                          />
                        </ValidationProvider>
                      </v-col>
                      <v-col>
                        <permission-control
                          permissionName="Document Type Create"
                        >
                          <v-btn
                            @click="createDocumentType"
                            :disabled="!newDocumentType.name"
                            depressed
                            small
                            class="text-white btn_blue btn_medium w-100"
                          >
                            Create
                          </v-btn>
                        </permission-control>
                      </v-col>
                    </v-row>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-row>
          </v-container>
        </v-card>
      </ValidationObserver>
    </v-container>
  </div>
</template>

<script>
import axios from '@/plugins/axios';
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import TableFilters from '@/components/base/TableFilters';
import pagination from '../../../components/pagination';
import confirmation from '../../../components/confirmation';
import noData from '../../../../../components/shared/noItem';

setInteractionMode('eager');
extend('required', { ...required, message: '{_field_} is required' });

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    TableFilters,
    pagination,
    noData,
  },
  data() {
    return {
      documenttypes: [],
      newDocumentType: { name: '' },
      editDocumentType: {},
      editDocumentTypeId: null,
      isLoading: false,
      show: false,
      filter: {},
      paginationOptions: {},
      columns: [
        { text: 'Name', value: 'name', align: 'left' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Actions', value: 'action', align: 'center' },
      ],
    };
  },
  methods: {
    createDocumentType() {
      const payload = { ...this.newDocumentType };
      this.isLoading = true;
      axios
        .post('document-types', payload)
        .then(() => {
          this.$toast.success('Document type created successfully.');
          this.refreshData();
          this.newDocumentType.name = '';
          this.$refs.observer.reset();
        })
        .catch((err) => {
          if (err.response?.data?.error) {
            this.$refs.observer.setErrors({
              new_name: err.response.data.error.name,
            });
          }
          this.$toast.error(
            err.response?.data?.title || 'Failed to create document type.',
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startEdit(item) {
      this.editDocumentType = { ...item };
      this.editDocumentTypeId = item.id;
    },
    cancelEdit() {
      this.editDocumentTypeId = null;
      this.editDocumentType = {};
      this.$refs.observer.reset();
    },
    updateDocumentType() {
      const ID = this.editDocumentTypeId;
      const payload = { ...this.editDocumentType };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.deleted_at;

      this.isLoading = true;
      axios
        .patch(`document-types/${ID}`, payload)
        .then(() => {
          this.$toast.success('Document type edited successfully');
          this.cancelEdit();
          this.refreshData();
        })
        .catch((err) => {
          if (err.response?.data?.error) {
            this.$refs.observer.setErrors({
              edit_name: err.response.data.error.name,
            });
          }
          this.$toast.error(
            err.response?.data?.title || 'Failed to edit document type',
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setPaginationOptions(data) {
      this.paginationOptions = data;
    },
    refreshData() {
      this.$refs.pagination.refreshData();
    },
    setResponseData(data) {
      this.documenttypes = data;
    },
    getStatusColor(active) {
      return active ? 'green' : 'grey';
    },
    openConfirmation(item) {
      confirmation.dialog = true;
      this.$confirm(
        `Do you want to ${item.is_active ? 'Archive' : 'Activate'} ${item.name}?`,
      ).then(() => {
        axios
          .get(`document-types/${item.id}/soft-delete`)
          .then(() => {
            this.$toast.success('Status updated');
            this.refreshData();
          })
          .catch(() => {
            this.$toast.error('Failed to update status.');
          });
      });
    },
  },
  computed: {
    headers() {
      return this.columns.filter((c) => c.show !== false);
    },
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro&display=swap');
.document-txt {
  font-family: 'Source Sans Pro', sans-serif;
}
.min-button::before {
  display: none;
}
</style>
